<template>
    <nav v-if="auth.profile" class="account-shortcuts" aria-label="Your account">
        <div class="account-actions">
        <Sheet v-model:open="menu.opened">
            <SheetTrigger as-child><button class="account-shortcut" aria-label="Open navigation menu"><Menu :size="24" /></button></SheetTrigger>
            <SheetContent side="left" class="mobile-drawer">
                <SheetTitle class="sr-only">Navigation</SheetTitle>
                <SheetDescription class="sr-only">Your account and main destinations.</SheetDescription>
                <NavigationContent @navigate="menu.opened = false" />
            </SheetContent>
        </Sheet>
        </div>
        <div class="account-actions">
            <RouterLink to="/friends" class="account-shortcut" :aria-label="requests ? `Friends, ${requests} pending requests` : 'Friends'" title="Friends">
                <Users :size="24" />
                <span v-if="requests" class="request-count" aria-hidden="true">{{ requests > 9 ? '9+' : requests }}</span>
            </RouterLink>
            <RouterLink to="/profile" class="account-shortcut account-avatar" aria-label="Account" title="Account"><img :src="auth.profile.profilePictureUrl || '/bots/player.svg'" alt="" @error="fallback" /></RouterLink>
        </div>
    </nav>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Users, Menu } from "lucide-vue-next";
import { Sheet, SheetTrigger, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import NavigationContent from "./NavigationContent.vue";
import { useMenuStore } from "@/stores/menu";
const menu = useMenuStore();
import { useAuthStore } from "@/stores/auth";
const auth = useAuthStore();
const requests = computed(() => auth.profile?.receivedFriendRequests?.length ?? 0);
function fallback(event: Event) {
    const image = event.target as HTMLImageElement;
    if (!image.src.endsWith("/bots/player.svg")) image.src = "/bots/player.svg";
}
</script>

<style>
/* Content starts below the 44px close button (top: max(8px, safe area)), which Home would otherwise sit under. */
.mobile-drawer { width: min(300px, 88vw); padding: calc(max(8px, env(safe-area-inset-top)) + 52px) 16px max(16px, env(safe-area-inset-bottom)); display: flex; flex-direction: column; overflow-y: auto; }
.mobile-drawer > button:last-child { width: 44px; height: 44px; display: grid; place-items: center; top: max(8px, env(safe-area-inset-top)); right: 8px; }
</style>
<style scoped>
.account-shortcuts { display: flex; align-items: center; justify-content: space-between; padding: max(8px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) 8px max(16px, env(safe-area-inset-left)); }
.account-actions { display: flex; gap: 12px; }
.account-shortcuts { background: linear-gradient(110deg, hsl(var(--primary) / .12), hsl(var(--card)) 65%); border-bottom: 1px solid hsl(var(--border)); }
.account-shortcut { position: relative; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; color: hsl(var(--muted-foreground)); }
.account-shortcut:hover, .account-shortcut.router-link-active { color: hsl(var(--primary)); background: hsl(var(--primary) / .1); }
.account-avatar img { width: 36px; height: 36px; object-fit: contain; border-radius: 6px; }
.request-count { position: absolute; right: -2px; top: -2px; min-width: 18px; height: 18px; padding: 0 4px; border-radius: 9px; background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); font-size: 11px; font-weight: 700; text-align: center; line-height: 18px; }
.account-shortcut:focus-visible { outline: 2px solid hsl(var(--primary)); outline-offset: 2px; }
</style>
